import React, { useState, useEffect } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import apiService, { getStaticAssetUrl } from '../services/api'; // Import the api service
import { takeSessionEndedNotice } from '../services/sessionManager';
import ForgotPasswordDialog from './auth/ForgotPasswordDialog';
import { TERMS_URL, PRIVACY_URL } from '../config/legalLinks';
// MUI Imports
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Link from '@mui/material/Link'; // Use MUI Link for external/policy links
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Alert from '@mui/material/Alert'; // For displaying errors
import AlertTitle from '@mui/material/AlertTitle';
import InfoIcon from '@mui/icons-material/Info';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';

const Login = ({ setToken }) => { // Assume setToken function is passed down to update auth state
  const [searchParams] = useSearchParams();
  const { code: pathCode } = useParams(); // Get code from URL path (e.g., /signup/:code)
  const [isLogin, setIsLogin] = useState(true); // Toggle between Login and Register
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false); // State for checkbox
  const [error, setError] = useState('');
  // Shown once after the server ended a session (security hardening batch 2).
  const [sessionNotice] = useState(() => takeSessionEndedNotice());
  const [forgotOpen, setForgotOpen] = useState(false);
  // Two-step sign-in (security batch 4): the challenge from the password step.
  const [mfaToken, setMfaToken] = useState(null);
  const [mfaCode, setMfaCode] = useState('');
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);
  const [mfaBusy, setMfaBusy] = useState(false);
  // "Trust this device" (1 Oct 2026): days offered by the server for this account; 0 = off.
  const [trustDays, setTrustDays] = useState(0);
  const [rememberDevice, setRememberDevice] = useState(false);

  const submitMfa = async (e) => {
    e.preventDefault();
    setError('');
    setMfaBusy(true);
    try {
      const entry = useRecoveryCode ? { recoveryCode: mfaCode } : { code: mfaCode.replace(/\s+/g, '') };
      const data = await apiService.verifyMfa(mfaToken, { ...entry, rememberDevice: trustDays > 0 && rememberDevice });
      const options = data.forcePasswordChange ? { forcePasswordChange: true } : {};
      setToken(data.token, options);
    } catch (err) {
      if (err.code === 'MFA_CHALLENGE_EXPIRED') {
        setMfaToken(null);
        setMfaCode('');
      }
      setError(err.message);
    } finally {
      setMfaBusy(false);
    }
  };
  const [registrationSuccess, setRegistrationSuccess] = useState(false);

  // Invitation handling
  const [invitationToken, setInvitationToken] = useState(null);
  const [signupCode, setSignupCode] = useState(null);
  const [invitationInfo, setInvitationInfo] = useState(null);

  // Platform settings for signup control
  const [allowPublicSignup, setAllowPublicSignup] = useState(true);

  // Fetch public platform settings (no auth required)
  useEffect(() => {
    const fetchPublicSettings = async () => {
      try {
        const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
        const response = await fetch(`${API_BASE_URL}/platform-settings/public`);
        if (response.ok) {
          const data = await response.json();
          setAllowPublicSignup(data.allowPublicSignup ?? true);
        }
      } catch (err) {
        console.error('Failed to fetch public settings:', err);
        // Default to allowing signup if fetch fails
        setAllowPublicSignup(true);
      }
    };
    fetchPublicSettings();
  }, []);

  // Check for invitation token or signup code in URL (supports both query params and path params)
  useEffect(() => {
    const invitation = searchParams.get('invitation');
    const code = searchParams.get('code') || pathCode; // Check query param first, then path param

    if (invitation) {
      setInvitationToken(invitation);
      setIsLogin(false); // Switch to registration mode
      validateInvitationToken(invitation);
    } else if (code) {
      setSignupCode(code);
      setIsLogin(false); // Switch to registration mode
      validateSignupCode(code);
    }
  }, [searchParams, pathCode]);

  const validateInvitationToken = async (token) => {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
      const response = await fetch(`${API_BASE_URL}/invitations/validate/${token}`);
      const data = await response.json();

      if (data.success) {
        setInvitationInfo(data.invitation);
        setName(data.invitation.invitedName || '');
        setEmail(data.invitation.invitedEmail || '');
      } else {
        setError(data.message || 'Invalid invitation link');
      }
    } catch (err) {
      setError('Failed to validate invitation link');
      console.error('Invitation validation error:', err);
    }
  };

  const validateSignupCode = async (code) => {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
      const response = await fetch(`${API_BASE_URL}/invitations/validate-code/${code}`);
      const data = await response.json();

      if (data.success) {
        setInvitationInfo(data.invitation);
      } else {
        setError(data.message || 'Invalid signup code');
      }
    } catch (err) {
      setError('Failed to validate signup code');
      console.error('Signup code validation error:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); // Clear previous errors

    // Prevent registration if terms are not agreed to
    if (!isLogin && !agreedToTerms) {
      setError('You must agree to the Terms of Service and Privacy Policy to register.');
      return;
    }

    const url = isLogin ? '/api/auth/login' : '/api/auth/register';
    const body = isLogin ? { email, password } : {
      name,
      email,
      password,
      invitationToken: invitationToken || undefined,
      signupCode: signupCode || undefined
    };

    try {
      if (isLogin) {
        const data = await apiService.login({ email, password });
        if (data && data.mfaRequired) {
          // Password accepted; now the code from the authenticator app.
          setMfaToken(data.mfaToken);
          setMfaCode('');
          setUseRecoveryCode(false);
          setTrustDays(Number(data.trustDays) || 0);
          setRememberDevice(false);
          return;
        }
        if (data && data.token) {
          // Check if there's a force password change flag
          const options = {
            ...(data.forcePasswordChange && { forcePasswordChange: true }),
            ...(data.mfaEnrollmentRequired && { mfaEnrollmentRequired: true })
          };
          setToken(data.token, options); // Pass options to App.js
          console.log('Login successful, token received.');
        } else {
          throw new Error('Login failed: No token received.');
        }
      } else {
        // Check if passwords match for registration
        if (password !== confirmPassword) {
          setError('Passwords do not match');
          return;
        }
        
        // Registration
        const data = await apiService.register({
          name,
          email,
          password,
          invitationToken: invitationToken || undefined,
          signupCode: signupCode || undefined
        });
        if (data && data.pending) {
          // Account awaits approval: no login until then (security batch 1).
          setRegistrationSuccess(true);
          setName('');
          setEmail('');
          setPassword('');
          setConfirmPassword('');
          setAgreedToTerms(false);
        } else if (data && data.token) {
          // If registered via invitation, auto-login
          if (invitationToken || signupCode) {
            console.log('Registration successful via invitation, logging in automatically.');
            // Store invitation token for onboarding flow resolution
            if (invitationToken) {
              localStorage.setItem('invitationToken', invitationToken);
              console.log('Stored invitation token for onboarding');
            }
            setToken(data.token);
          } else {
            // Self-registration: show pending message instead
            setRegistrationSuccess(true);
            // Clear form
            setName('');
            setEmail('');
            setPassword('');
            setConfirmPassword('');
            setAgreedToTerms(false);
            console.log('Registration successful, account pending approval.');
          }
        } else {
          throw new Error('Registration failed: No token received.');
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to authenticate');
      console.error('Authentication error:', err);
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Paper elevation={3} sx={{ marginTop: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 3 }}>
        {/* Service Group Logo - displayed when configured for invitation */}
        {invitationInfo?.showServiceGroupLogo && invitationInfo?.serviceGroupLogoUrl && !isLogin && (
          <Box sx={{ mb: 2, textAlign: 'center' }}>
            <Box
              component="img"
              src={getStaticAssetUrl(invitationInfo.serviceGroupLogoUrl)}
              alt={invitationInfo.serviceGroupName || 'Organization logo'}
              sx={{
                maxWidth: 200,
                maxHeight: 100,
                objectFit: 'contain'
              }}
            />
          </Box>
        )}
        <Typography component="h1" variant="h5">
          {isLogin ? 'Sign In' : 'Sign Up'}
        </Typography>
        {sessionNotice && isLogin && (
          <Alert severity="info" sx={{ mt: 2, width: '100%' }}>{sessionNotice}</Alert>
        )}
        {/* Parent-initiated invitation alert (adolescent signup) */}
        {invitationInfo?.parentInitiated && !isLogin && (
          <Alert
            severity="info"
            icon={<FamilyRestroomIcon />}
            sx={{ mt: 2, width: '100%' }}
          >
            <AlertTitle>Invited by {invitationInfo.parentName}</AlertTitle>
            Your parent has invited you to join AIMHI. After signing up, your accounts will
            be linked so they can support your progress and submit check-ins on your behalf.
          </Alert>
        )}
        {/* Standard invitation info alert */}
        {invitationInfo && !invitationInfo.parentInitiated && !isLogin && (
          <Alert severity="info" sx={{ mt: 2, width: '100%' }}>
            {invitationInfo.invitedName ? `Welcome ${invitationInfo.invitedName}! ` : 'Welcome! '}
            {invitationInfo.welcomeMessage || 'Complete your registration to get started.'}
            {invitationInfo.serviceGroupName && ` You'll be added to ${invitationInfo.serviceGroupName}.`}
          </Alert>
        )}
        {mfaToken && isLogin && (
          <Box component="form" onSubmit={submitMfa} noValidate sx={{ mt: 2, width: '100%' }}>
            <Typography variant="body1" sx={{ mb: 1 }}>
              {useRecoveryCode ? 'Enter one of your recovery codes.' : 'Enter the 6-digit code from your authenticator app.'}
            </Typography>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <TextField
              id="mfa-login-code"
              autoFocus fullWidth margin="normal"
              label={useRecoveryCode ? 'Recovery code' : '6-digit code'}
              inputMode={useRecoveryCode ? 'text' : 'numeric'}
              autoComplete="one-time-code"
              value={mfaCode}
              onChange={e => setMfaCode(e.target.value)}
            />
            {trustDays > 0 && (
              <Box sx={{ mt: 1 }}>
                <FormControlLabel
                  control={<Checkbox id="mfa-trust-device" checked={rememberDevice} onChange={e => setRememberDevice(e.target.checked)} />}
                  label={`Trust this device for ${trustDays} days`}
                />
                <Typography variant="caption" color="text.secondary" component="p" sx={{ ml: 4, mt: -0.5 }}>
                  You won't need a code here until then, but you will still need your password. Don't tick this on a shared or public computer.
                </Typography>
              </Box>
            )}
            <Button type="submit" fullWidth variant="contained" sx={{ mt: 2, mb: 1 }} disabled={mfaBusy || !mfaCode.trim()}>
              {mfaBusy ? 'Checking…' : 'Sign In'}
            </Button>
            <Button fullWidth variant="text" onClick={() => { setUseRecoveryCode(!useRecoveryCode); setMfaCode(''); setError(''); }}>
              {useRecoveryCode ? 'Use a code from my app instead' : 'Lost your phone? Use a recovery code'}
            </Button>
            <Button fullWidth variant="text" onClick={() => { setMfaToken(null); setMfaCode(''); setError(''); }}>
              Back
            </Button>
          </Box>
        )}
        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1, display: mfaToken && isLogin ? 'none' : 'block' }}>
          {!isLogin && (
            <TextField
              margin="normal"
              required
              fullWidth
              id="name"
              label="Name"
              name="name"
              autoComplete="name"
              autoFocus={!isLogin} // Autofocus name field on register
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          )}
          <TextField
            margin="normal"
            required
            fullWidth
            id="email"
            label="Email Address"
            name="email"
            autoComplete="email"
            autoFocus={isLogin} // Autofocus email field on login
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={!isLogin && invitationToken} // Disable email for individual invitations
            data-testid="email-input"
          />
          <TextField
            margin="normal"
            required
            fullWidth
            name="password"
            label="Password"
            type="password"
            id="password"
            autoComplete={isLogin ? "current-password" : "new-password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            helperText={!isLogin ? "Password must be at least 8 characters long with 1 uppercase letter, 1 lowercase letter, 1 number, and 1 special character" : ""}
            data-testid="password-input"
          />

          {!isLogin && (
            <TextField
              margin="normal"
              required
              fullWidth
              name="confirmPassword"
              label="Confirm Password"
              type="password"
              id="confirmPassword"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          )}
          {!isLogin && (
             <FormControlLabel
               control={
                 <Checkbox
                   value="agree"
                   color="primary"
                   checked={agreedToTerms}
                   onChange={(e) => setAgreedToTerms(e.target.checked)}
                   required
                 />
               }
               label={
                 <Typography variant="body2">
                   I agree to the{' '}
                   <Link href={TERMS_URL} target="_blank" rel="noopener noreferrer">
                     Terms of Service
                   </Link>{' '}
                   and{' '}
                   <Link href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">
                     Privacy Policy
                   </Link>
                   .
                 </Typography>
               }
               sx={{ mt: 1, mb: 1 }}
             />
          )}
          {error && (
            <Alert severity="error" sx={{ width: '100%', mt: 1, mb: 1 }} data-testid="login-error">
              {error}
            </Alert>
          )}
          
          {registrationSuccess && (
            <Alert 
              severity="info" 
              icon={<InfoIcon />}
              sx={{ width: '100%', mt: 1, mb: 1 }}
            >
              Thank you for registering with AIMHI. We will contact you when your account has been activated.
            </Alert>
          )}
          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={!isLogin && !agreedToTerms}
            sx={{ mt: 3, mb: 2 }}
            data-testid="login-button"
          >
            {isLogin ? 'Sign In' : 'Sign Up'}
          </Button>
          {isLogin && (
            <Button fullWidth variant="text" onClick={() => setForgotOpen(true)} data-testid="forgot-password-link">
              Forgot your password?
            </Button>
          )}
          <ForgotPasswordDialog open={forgotOpen} onClose={() => setForgotOpen(false)} initialEmail={email} />
          {/* Show toggle button if:
              1. Public signup is allowed, OR
              2. User has an invitation token or signup code (always allow them to switch)
              3. User is currently in signup mode (always allow them to switch back to login)
          */}
          {(allowPublicSignup || invitationToken || signupCode || !isLogin) && (
            <Button
              fullWidth
              variant="text" // Use text variant for less emphasis
              onClick={() => {
                setIsLogin(!isLogin);
                setError('');
                setAgreedToTerms(false);
                setRegistrationSuccess(false);
              }}
            >
              {isLogin ? "Don't have an account? Sign Up" : "Already have an account? Sign In"}
            </Button>
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default Login;
